import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Check } from 'lucide-react';
import { services } from '../utils/mockData';
import { useLanguage } from '../contexts/LanguageContext';

const ServicesShowcase = () => {
  const { t, language } = useLanguage();

  return (
    <section className="py-32 bg-gradient-to-b from-navy-50 to-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Section Header */}
        <div className="mb-24 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-4"
          >
            <span className="text-sm font-semibold text-eucalyptus-600 tracking-widest uppercase">
              {t('What We Deliver', '我们提供的服务')}
            </span>
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-navy-900 mb-6"
          >
            {t('Comprehensive Care Services', '全方位护理服务')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t(
              'A full spectrum of in-home support, delivered by trained, vetted, bilingual professionals',
              '经过培训、审查的双语专业人员提供全方位的居家支持服务'
            )}
          </motion.p>
        </div>

        {/* Services Grid */}
        <div className="space-y-32">
          {services.map((service, index) => (
            <ServiceCard 
              key={service.id} 
              service={service} 
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ServiceCard = ({ service, index }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  const { language } = useLanguage();

  const isEven = index % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 80 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 1, ease: [0.215, 0.61, 0.355, 1] }}
      className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
        isEven ? '' : 'lg:grid-flow-dense'
      }`}
      data-testid={`service-card-${index}`}
    >
      {/* Image with clipped frame effect */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={inView ? { scale: 1, opacity: 1 } : {}}
        transition={{ duration: 1.2, delay: 0.2 }}
        className={`relative group ${isEven ? '' : 'lg:col-start-2'}`}
      >
        <div className="relative overflow-hidden rounded-3xl aspect-[4/3] shadow-2xl">
          {/* Number overlay */}
          <div className="absolute top-8 left-8 z-10">
            <motion.div
              initial={{ opacity: 0, scale: 0 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="w-16 h-16 bg-white/20 backdrop-blur-md border border-white/30 rounded-2xl flex items-center justify-center"
            >
              <span className="text-2xl font-bold text-white">
                {service.number}
              </span>
            </motion.div>
          </div>

          {/* Featured badge for concierge service */}
          {service.id === 0 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="absolute top-8 right-8 z-10"
            >
              <div className="px-4 py-2 bg-eucalyptus-500 text-white rounded-full text-sm font-semibold shadow-lg">
                {language === 'zh' ? '特色服务' : 'Featured'}
              </div>
            </motion.div>
          )}

          {/* Image */}
          <motion.img
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.6 }}
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900/60 via-transparent to-transparent group-hover:from-eucalyptus-900/60 transition-all duration-500" />
        </div>
      </motion.div>

      {/* Content */}
      <div className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}>
        <motion.div
          initial={{ opacity: 0, x: isEven ? -50 : 50 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="mb-4">
            <span className="text-sm font-semibold text-eucalyptus-600 tracking-widest uppercase">
              {language === 'zh' ? `服务 ${service.number}` : `Service ${service.number}`}
            </span>
          </div>
          <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-navy-900 mb-6">
            {language === 'zh' && service.titleZh ? service.titleZh : service.title}
          </h3>
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-8">
            {language === 'zh' && service.descriptionZh ? service.descriptionZh : service.description}
          </p>

          {/* Features list */}
          <ul className="space-y-4">
            {(language === 'zh' && service.featuresZh ? service.featuresZh : service.features).map((feature, i) => (
              <motion.li
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                className="flex items-center gap-3 text-lg text-gray-700"
              >
                <div className="flex-shrink-0 w-6 h-6 rounded-full bg-eucalyptus-100 flex items-center justify-center">
                  <Check className="w-4 h-4 text-eucalyptus-600" />
                </div>
                {feature}
              </motion.li>
            ))}
          </ul>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ServicesShowcase;
